import { Badge, Box, Group, Paper, Progress, Stack, Text, Timeline, Title } from "@mantine/core";
import { IconCheck, IconClock, IconCircleDot } from "@tabler/icons-react";
import { useMemo } from "react";

interface Milestone {
  name: string;
  date: string;
  status: "completed" | "in_progress" | "planned";
}

interface ProjectTimelineProps {
  projectStartDate?: string | Date;
  projectEndDate?: string | Date;
  milestones?: Milestone[];
}

/**
 * ProjectTimeline - Visual timeline showing project progress with milestones
 *
 * Displays:
 * - Project start and end dates
 * - Key milestones with completion status
 * - Current date marker
 * - Overall progress percentage
 * - Schedule status (on track / behind / ahead)
 */
export function ProjectTimeline({
  projectStartDate,
  projectEndDate,
  milestones,
}: ProjectTimelineProps) {
  // Demo milestones if none provided
  const defaultMilestones: Milestone[] = [
    { name: "Foundation", date: "2025-10-15", status: "completed" },
    { name: "Framing", date: "2025-11-01", status: "completed" },
    { name: "Rough-In", date: "2025-11-20", status: "in_progress" },
    { name: "Drywall", date: "2025-12-10", status: "planned" },
    { name: "Finishes", date: "2026-01-15", status: "planned" },
  ];

  const timelineMilestones = milestones || defaultMilestones;

  // Calculate progress and schedule status
  const { progressPercentage, scheduleStatus, daysBehind, daysAhead } = useMemo(() => {
    const now = new Date();
    const startDate = projectStartDate ? new Date(projectStartDate) : new Date(timelineMilestones[0]?.date || now);
    const endDate = projectEndDate
      ? new Date(projectEndDate)
      : new Date(timelineMilestones[timelineMilestones.length - 1]?.date || now);

    const totalDays = Math.max(
      1,
      Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    );
    const elapsedDays = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const timeProgress = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));

    const completedCount = timelineMilestones.filter((m) => m.status === "completed").length;
    const totalMilestones = timelineMilestones.length;
    const milestoneProgress = (completedCount / totalMilestones) * 100;

    // Determine schedule status
    const progressDiff = milestoneProgress - timeProgress;
    let status: "on_track" | "behind" | "ahead";
    let behind = 0;
    let ahead = 0;

    if (progressDiff < -10) {
      // More than 10% behind
      status = "behind";
      behind = Math.round((Math.abs(progressDiff) / 100) * totalDays);
    } else if (progressDiff > 10) {
      // More than 10% ahead
      status = "ahead";
      ahead = Math.round((progressDiff / 100) * totalDays);
    } else {
      status = "on_track";
    }

    return {
      progressPercentage: Math.round(milestoneProgress),
      scheduleStatus: status,
      daysBehind: behind,
      daysAhead: ahead,
    };
  }, [projectStartDate, projectEndDate, timelineMilestones]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = () => {
    if (scheduleStatus === "ahead") return "green";
    if (scheduleStatus === "behind") return "red";
    return "blue";
  };

  const getStatusText = () => {
    if (scheduleStatus === "ahead") return `${daysAhead} days ahead`;
    if (scheduleStatus === "behind") return `${daysBehind} days behind`;
    return "On track";
  };

  const getStatusIcon = (status: Milestone["status"]) => {
    switch (status) {
      case "completed":
        return <IconCheck size={18} />;
      case "in_progress":
        return <IconClock size={18} />;
      case "planned":
        return <IconCircleDot size={18} />;
    }
  };

  const getStatusColor2 = (status: Milestone["status"]) => {
    switch (status) {
      case "completed":
        return "green";
      case "in_progress":
        return "blue";
      case "planned":
        return "gray";
    }
  };

  return (
    <Paper p="xl" withBorder>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3}>Project Progress</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Timeline and milestones
            </Text>
          </div>
          <Badge color={getStatusColor()} size="lg" variant="light">
            {getStatusText()}
          </Badge>
        </Group>

        {/* Progress Bar */}
        <Box>
          <Group justify="space-between" mb={8}>
            <Text size="sm" fw={500}>
              Overall Progress
            </Text>
            <Text size="sm" fw={600} c={getStatusColor()}>
              {progressPercentage}%
            </Text>
          </Group>
          <Progress value={progressPercentage} color={getStatusColor()} size="lg" radius="md" />
        </Box>

        {/* Timeline */}
        <Timeline active={timelineMilestones.findIndex((m) => m.status === "in_progress")} bulletSize={24} lineWidth={2}>
          {timelineMilestones.map((milestone, index) => (
            <Timeline.Item
              key={index}
              bullet={getStatusIcon(milestone.status)}
              title={milestone.name}
              color={getStatusColor2(milestone.status)}
            >
              <Text size="sm" c="dimmed" mt={4}>
                {formatDate(milestone.date)}
              </Text>
              {milestone.status === "in_progress" && (
                <Badge color="blue" size="sm" mt={8} variant="light">
                  In Progress
                </Badge>
              )}
              {milestone.status === "completed" && (
                <Badge color="green" size="sm" mt={8} variant="light">
                  Completed
                </Badge>
              )}
            </Timeline.Item>
          ))}
        </Timeline>

        {/* Today Marker */}
        <Paper p="md" withBorder style={{ borderStyle: "dashed", backgroundColor: "var(--mantine-color-blue-0)" }}>
          <Group gap="xs">
            <IconClock size={16} style={{ color: "var(--mantine-color-blue-6)" }} />
            <Text size="sm" fw={500} c="blue">
              Today: {formatDate(new Date().toISOString())}
            </Text>
          </Group>
        </Paper>
      </Stack>
    </Paper>
  );
}
